import { InfiniteData, UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';

import {
  ExternalSharesQuery,
  ExternalSharesQueryVariables,
  UpdateExternalShareMutation,
  UpdateExternalShareMutationVariables,
  useExternalSharesQuery,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = UpdateExternalShareMutation;
type MutationVariables = UpdateExternalShareMutationVariables;
type QueryData = InfiniteData<ExternalSharesQuery>;
type QueryVariables = ExternalSharesQueryVariables;

const useUpdateExternalShares = (variables: QueryVariables) => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const queryKey = useExternalSharesQuery.getKey(variables);

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async ({ data, share_id }) => {
      await queryClient.cancelQueries(queryKey);

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.pages.forEach(page => {
            const { externalShares } = page;
            const { results } = externalShares;
            const index = results?.findIndex(share => share.share_id === share_id);

            if (index !== -1 && data.engage_content_created === false) {
              externalShares?.results?.splice(index, 1);
            }
          });
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
  };

  return mutationOptions;
};

export default useUpdateExternalShares;
